/**
 * Common types for SDK collectors.
 *
 * A `Collector<T>` is a single fingerprint signal source. The orchestrator
 * runs collectors in parallel via `Promise.allSettled` with per-collector
 * timeouts; one collector failing or hanging never fails the whole call.
 */

export interface Collector<T> {
  /** Stable name for telemetry / debug. */
  readonly name: string;
  /** Bumped when the signal's algorithm changes (informational). */
  readonly version: number;
  /** Override the orchestrator's default timeout (ms). */
  readonly timeoutMs?: number;
  /** Returns the signal value, or undefined when unavailable. May reject. */
  collect(ctx: CollectorContext): Promise<T | undefined> | T | undefined;
}

/**
 * Shared context passed into each collector. Pre-resolved upstream signals
 * (e.g. browser detection) live here so downstream collectors don't re-detect.
 */
export interface CollectorContext {
  isBrowser: boolean;
  browser?: BrowserInfo;
}

export interface BrowserInfo {
  isBrave: boolean;
  isSafari: boolean;
  isFirefox: boolean;
  isIosChrome: boolean;
  ua: string;
  brands: string[];
  family: string;
  version: string;
  engine: string;
}

/** Safe SSR / non-browser guard. Imported by every collector. */
export const isBrowser: boolean =
  typeof window !== "undefined" && typeof document !== "undefined";
